import classNames from "classnames";
import React from "react";
import { bindActionCreators } from "redux";
import { connect } from "react-redux";

import {
  Form,
  FormMessage,
  Accordion,
  Button,
  Grid,
  Message,
  BillRow,
  EditableBillRow,
  OrderFullDecorator,
  st,
} from "lib/index";

import {
  stateSelector,
  addCustomItem,
  updateCustomItem,
  removeCustomItem,
  updateOrderedField,
  updateOrderedItem,
  removeOrderParticipant,
  updateOrder,
  validate,
  submit,
} from "./index";
import * as FormControl from "lib/components/FormControl";

@connect(
  (state) => ({
    initialOrder: stateSelector(state).initialOrder,
    order: stateSelector(state).order,
    valid: stateSelector(state).valid,
    submitLoading: stateSelector(state).submitLoading,
    saved: stateSelector(state).saved,
    errors: stateSelector(state).errors,
  }),
  (dispatch) =>
    bindActionCreators(
      {
        addCustomItem,
        updateCustomItem,
        removeCustomItem,
        updateOrderedField,
        updateOrderedItem,
        removeOrderParticipant,
        updateOrder,
        validate,
        submit,
      },
      dispatch
    )
)
export default class EditOrderItemsModalForm extends React.PureComponent {
  componentDidUpdate(prevProps) {
    const { saved, initialOrder, onSave } = this.props;
    if (saved && !prevProps.saved) {
      onSave(initialOrder);
    }
  }

  handlePropChange(prop, value) {
    const { order, updateOrder } = this.props;
    updateOrder({
      ...order,
      [prop]: value,
    });
  }

  handleSubmit(event) {
    const { submit } = this.props;
    event.preventDefault();
    submit();
  }

  render() {
    const {
      initialOrder,
      order,
      errors,
      valid,
      validate,
      submitLoading,
      addCustomItem,
      updateCustomItem,
      removeCustomItem,
      updateOrderedField,
      updateOrderedItem,
      removeOrderParticipant,
    } = this.props;

    return (
      <Form onSubmit={this.handleSubmit.bind(this)} error={!valid} loading={submitLoading}>
        {!!errors.base && <FormMessage error={errors.base} />}

        {!!order.order_customer_participants.find((p) => p.is_extra) && (
          <Message warning visible>
            {I18n.t("papp.orders.EditOrderItemsModal.extra_participants_existing")}
          </Message>
        )}

        {order.installments.length > 1 &&
          !st.MoneySum.isEqual(initialOrder.total_amount_sum, order.total_amount_sum) && (
            <Message warning visible>
              {I18n.t("papp.orders.EditOrderItemsModal.installments_require_manual_change_warning")}
            </Message>
          )}

        <Grid>
          <Grid.Column width={7}>
            <h3>{I18n.humanAttributeName("order", "ordered_product_pricelist_items")}</h3>
            {order.ordered_product_pricelist_items.map((orderedItem) => (
              <FormControl.OrderedProductPricelistItemFormGroup
                key={orderedItem.uuid}
                value={orderedItem}
                onBlur={validate}
                onChange={updateOrderedItem}
              />
            ))}

            {order.priced_ordered_fields.length > 0 && (
              <div className="u-marginBottom">
                <h3>{I18n.humanAttributeName("order", "product_options")}</h3>
                {order.priced_ordered_fields.map((orderedField) => (
                  <FormControl.OrderedProductOrderFieldFormGroup
                    key={orderedField.uuid}
                    {...FormControl.addProps(`order.ordered_product_order_fields[${orderedField.uuid}].value`, {
                      errors,
                    })}
                    value={orderedField}
                    onBlur={validate}
                    onChange={updateOrderedField}
                    inputRequired={false}
                  />
                ))}
              </div>
            )}

            {order.order_customer_participants.length > 0 && (
              <div>
                <h3>{I18n.humanAttributeName("order", "order_customer_participants")}</h3>
                {order.order_customer_participants.map((participant) => {
                  return [
                    <h4>
                      {participant.real_display_name}
                      {participant.is_extra && (
                        <FormControl.DeleteRowButton
                          absolute={false}
                          onClick={() => removeOrderParticipant(participant)}
                        />
                      )}
                    </h4>,
                    ...participant.ordered_product_order_fields
                      .filter((orderedField) => orderedField.product_order_field.has_pricing)
                      .map((orderedField) => (
                        <FormControl.OrderedProductOrderFieldFormGroup
                          key={orderedField.uuid}
                          {...FormControl.addProps(
                            `order.order_customer_participants[${participant.uuid}].ordered_product_order_fields[${orderedField.uuid}].value`,
                            { errors }
                          )}
                          value={orderedField}
                          onBlur={validate}
                          onChange={updateOrderedField}
                          inputRequired={false}
                        />
                      )),
                  ];
                })}
              </div>
            )}

            {order.agent && (
              <div className="u-marginTop">
                <h3>{I18n.humanAttributeName("order", "agent")}</h3>
                <FormControl.Select
                  value={order.agent.id}
                  options={[[order.agent.id, order.agent.name]]}
                  fluid
                  disabled
                />

                <FormControl.MoneyFormGroup
                  inlineLabel
                  disabled={!["global_admin", "main_admin", "editor_admin"].includes(st.authorize.member.role)}
                  label={I18n.humanAttributeName("order", "agent_commision")}
                  {...FormControl.addProps(`order.agent_commision_amount`, {
                    errors,
                  })}
                  value={order.agent_commision_amount}
                  onChange={this.handlePropChange.bind(this, "agent_commision_amount")}
                  min={0}
                  defaultCurrency={order.product_price_currency}
                />
              </div>
            )}
          </Grid.Column>

          <Grid.Column width={9}>
            <h3>{I18n.t("papp.orders.EditOrderItemsModal.order_items_title")}</h3>
            <table className="ui table celled very basic clear unstackable">
              <thead>
                <tr>
                  <th>{I18n.t("papp.orders.EditOrderItemsModal.order_item_index")}</th>
                  <th className="right aligned four wide">
                    {I18n.t("papp.orders.EditOrderItemsModal.order_item_unit_price")}
                  </th>
                  <th className="center aligned collapsing">
                    {I18n.t("papp.orders.EditOrderItemsModal.order_item_quantity")}
                  </th>
                  <th className="right aligned collapsing">
                    {I18n.t("papp.orders.EditOrderItemsModal.order_item_total_price")}
                  </th>
                </tr>
              </thead>
              <tbody>
                {order.order_items
                  .filter((item) => !item.is_new_custom_item)
                  .map((item) => (
                    <BillRow
                      key={item.uuid}
                      className={!item.id && "positive"}
                      name={item.name}
                      quantity={item.quantity}
                      unitPrice={st.formatMoney(item.unit_price)}
                      totalPrice={st.formatMoney(item.total_price)}
                    />
                  ))}
                {order.order_items
                  .filter((item) => item.is_new_custom_item)
                  .map((item) => (
                    <EditableBillRow
                      key={item.uuid}
                      className="positive"
                      valid={errors.hasValidProp(`order.order_items[${item.uuid}]`)}
                      item={item}
                      defaultCurrency={order.product_price_currency}
                      onBlur={validate}
                      onChange={updateCustomItem}
                      onRemove={removeCustomItem.bind(null, item)}
                    />
                  ))}
                <BillRow
                  name={
                    <a onClick={addCustomItem} className="ui button basic blue">
                      {I18n.t("papp.orders.EditOrderItemsModal.add_new_order_item")}
                    </a>
                  }
                />
              </tbody>
              <tfoot className="full-width">
                <tr>
                  <th className="summary">{I18n.t("fe.order_details.to_be_paid_total_amount")}</th>
                  <th colSpan="3" className="right aligned summary">
                    {st.formatMoney(order.total_amount_sum)}
                  </th>
                </tr>
              </tfoot>
            </table>
          </Grid.Column>
        </Grid>

        <button type="submit" disabled={submitLoading} hidden />
      </Form>
    );
  }
}



// WEBPACK FOOTER //
// ./src/modules/orders/EditOrderItemsModal/EditOrderItemsModalForm.jsx